import { useCallback, useMemo } from 'react';
import { MutationHookOptions } from '@apollo/client/react';
import { useMutation } from '@apollo/client/react/hooks';
import isNil from 'lodash-es/isNil';
import { gql } from '@yi/core';

import { useUserFavoritesByIds } from '~/src/common/favorites/useUserFavoritesByIds';

interface Args {
  contentId: string;
  isTeacher?: boolean;
  isPlaylist?: boolean;
  onAdd?: (contentId: string) => void;
  onRemove?: (contentId: string) => void;
}

const addMutationOpts:
  | MutationHookOptions<
      gql.typesClient.Mutate_AddFavorite,
      gql.typesClient.Mutate_AddFavoriteVariables
    >
  | undefined = {
  // If graphQL error, onError is necessary for mutation promise to resolve
  onError: (error: Error) => {
    console.warn(error);
  },
  // TODO implement refetch queries
  // refetchQueries: [{ query: gql.Query_CurrentUser_AllFavorites }],
};

const removeMutationOpts:
  | MutationHookOptions<
      gql.typesClient.Mutate_RemoveFavorite,
      gql.typesClient.Mutate_RemoveFavoriteVariables
    >
  | undefined = {
  // If graphQL error, onError is necessary for mutation promise to resolve
  onError: (error: Error) => {
    console.warn(error);
  },
  // TODO implement refetch queries
  // refetchQueries: [{ query: gql.Query_CurrentUser_AllFavorites }],
};

type AddFavorite = gql.typesClient.Mutate_AddFavorite;
type AddFavoriteVars = gql.typesClient.Mutate_AddFavoriteVariables;
type RemoveFavorite = gql.typesClient.Mutate_RemoveFavorite;
type RemoveFavoriteVars = gql.typesClient.Mutate_RemoveFavoriteVariables;

export const useAddRemoveFavorite = ({
  contentId,
  isTeacher,
  isPlaylist,
  onAdd,
  onRemove,
}: Args) => {
  const {
    favorites,
    loading: isLoadingFavorites,
    refetch: refetchFavorites,
  } = useUserFavoritesByIds([contentId]);

  const [mutateAddFavorite, addResult] = useMutation<AddFavorite, AddFavoriteVars>(
    gql.Mutate_AddFavorite,
    addMutationOpts,
  );

  const [mutateRemoveFavorite, removeResult] = useMutation<RemoveFavorite, RemoveFavoriteVars>(
    gql.Mutate_RemoveFavorite,
    removeMutationOpts,
  );

  const variables: AddFavoriteVars | RemoveFavoriteVars = useMemo(
    () => ({
      content_id: contentId,
      type: isPlaylist
        ? gql.typesClient.ToggleFavoriteType.playlist
        : isTeacher
        ? gql.typesClient.ToggleFavoriteType.teacher
        : gql.typesClient.ToggleFavoriteType.post,
    }),
    [contentId, isPlaylist, isTeacher],
  );

  const isFavorite = useMemo(() => {
    if (isNil(favorites)) return false;
    const isFound = !!favorites.find(c => c && c.content_id === contentId);
    return isFound;
  }, [contentId, favorites]);

  const addFavorite = useCallback(async () => {
    return mutateAddFavorite({ variables }).then(result => {
      refetchFavorites();
      if (result.data?.addFavorite.status === true && typeof onAdd === 'function') {
        onAdd(contentId);
        return result;
      }
      return result;
    });
  }, [contentId, mutateAddFavorite, onAdd, refetchFavorites, variables]);

  const removeFavorite = useCallback(() => {
    mutateRemoveFavorite({ variables }).then(result => {
      refetchFavorites();
      if (result.data?.removeFavorite.status === true && typeof onRemove === 'function') {
        onRemove(contentId);
        return result;
      }
      return result;
    });
  }, [contentId, mutateRemoveFavorite, onRemove, refetchFavorites, variables]);

  return {
    addFavorite,
    removeFavorite,
    loading: addResult.loading || removeResult.loading || isLoadingFavorites,
    isFavorite,
  };
};
